// ============================================================
//  Page /sessions — liste des sessions
// ============================================================

import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import * as api from "../lib/api";
import type { Session, SessionInput, SessionStatut } from "../lib/types";

const STALINGRAD_ID = "11111111-1111-1111-1111-111111111111";

const STATUT_LABEL: Record<SessionStatut, { text: string; cls: string }> = {
  preparee: { text: "Préparée", cls: "stat-prep" },
  en_cours: { text: "● En cours", cls: "stat-active" },
  terminee: { text: "Terminée", cls: "stat-done" },
};

export function SessionsList() {
  const [items, setItems] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listSessions();
      setItems(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); }, []);

  async function onCreate(input: SessionInput) {
    await api.createSession(input);
    setShowForm(false);
    await reload();
  }

  async function onDelete(s: Session, e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (s.statut === "en_cours") {
      alert("Termine la session avant de la supprimer.");
      return;
    }
    if (!confirm(`Supprimer la session "${s.nom}" ?\nLes groupes et progressions associés seront supprimés en cascade.`)) return;
    try {
      await api.deleteSession(s.id);
      await reload();
    } catch (err) {
      alert("Suppression échouée : " + (err as Error).message);
    }
  }

  return (
    <div className="questions-page">
      <header className="questions-head">
        <div>
          <h1>Sessions</h1>
          <p className="questions-sub">
            {items.length} session{items.length > 1 ? "s" : ""} · plusieurs peuvent être actives en parallèle
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary">+ Nouvelle session</button>
      </header>

      {error && <div className="page-error">Erreur : {error}</div>}
      {loading ? (
        <div className="page-loading">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="questions-empty">
          <p>Aucune session encore.</p>
          <button onClick={() => setShowForm(true)} className="btn-primary">+ Créer la première</button>
        </div>
      ) : (
        <div className="parcours-grid">
          {items.map((s) => {
            const stat = STATUT_LABEL[s.statut];
            return (
              <Link key={s.id} to={`/sessions/${s.id}`} className="session-card">
                <div className="parcours-head">
                  <h3>{s.nom}</h3>
                  <span className={`badge ${stat.cls}`}>{stat.text}</span>
                </div>
                <div className="session-meta">
                  📅 {new Date(s.date_debut).toLocaleString("fr-FR", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </div>
                {s.statut !== "en_cours" && (
                  <button
                    onClick={(e) => onDelete(s, e)}
                    className="session-delete"
                    title="Supprimer cette session"
                    aria-label="Supprimer"
                  >🗑</button>
                )}
              </Link>
            );
          })}
        </div>
      )}

      {showForm && (
        <SessionForm onSubmit={onCreate} onCancel={() => setShowForm(false)} />
      )}
    </div>
  );
}

function SessionForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (input: SessionInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [nom, setNom] = useState("");
  const [dateDebut, setDateDebut] = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  });
  const [motSecret, setMotSecret] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        lieu_id: STALINGRAD_ID,
        nom: nom.trim(),
        date_debut: new Date(dateDebut).toISOString(),
        mot_secret_base: motSecret.trim(),
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-form" onClick={(e) => e.stopPropagation()}>
        <h2>Nouvelle session</h2>
        <form onSubmit={handle}>
          <label>
            <span>Nom *</span>
            <input
              type="text"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              required
              autoFocus
              placeholder="Ex : Sortie scolaire 15 mai 2026"
              maxLength={200}
            />
          </label>
          <label>
            <span>Date / heure de début *</span>
            <input
              type="datetime-local"
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
              required
            />
          </label>
          <label>
            <span>Mot secret de la base * <small>(à donner à l'équipe à la base)</small></span>
            <input
              type="text"
              value={motSecret}
              onChange={(e) => setMotSecret(e.target.value)}
              required
              placeholder="Ex : ouvre-toi"
              minLength={3}
              maxLength={100}
            />
          </label>
          {error && <p className="form-error">✗ {error}</p>}
          <div className="modal-actions">
            <button type="button" onClick={onCancel} className="btn-secondary">Annuler</button>
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? "Création…" : "Créer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
